'use client'

import { Menu, Tooltip } from '@mantine/core'
import { SUPPORTED_CURRENCIES } from '@partment/types'
import { RiArrowDownSLine, RiCheckboxCircleFill } from '@remixicon/react'
import { useEffect, useState } from 'react'
import { useCurrency } from '../..'

interface CurrencySelectorProps {
  className?: string
  width?: number
}

const CurrencySelector: React.FC<CurrencySelectorProps> = ({
  className = '',
  width = 160,
}) => {
  const { currency, changeCurrency, isHydrated } = useCurrency()
  const [opened, setOpened] = useState(false)
  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  const showActualCurrency = isMounted && isHydrated
  const selected = showActualCurrency
    ? SUPPORTED_CURRENCIES.find((c) => c.code === currency) ||
      SUPPORTED_CURRENCIES.find((c) => c.code === 'USD')
    : SUPPORTED_CURRENCIES.find((c) => c.code === 'USD')

  const handleCurrencyChange = (item: (typeof SUPPORTED_CURRENCIES)[0]) => {
    changeCurrency(item.code)
    setOpened(false)
  }

  const menuItems = SUPPORTED_CURRENCIES.map((item) => (
    <Menu.Item key={item.code} onClick={() => handleCurrencyChange(item)}>
      <div className='flex justify-between items-center w-full'>
        <div className='flex gap-2'>
          <span className='text-[#101828] text-[12px]'>{item.symbol}</span>
          <span className='text-[#101828] text-[12px]'>{item.code}</span>
        </div>
        {selected?.code === item.code && <RiCheckboxCircleFill size={16} />}
      </div>
    </Menu.Item>
  ))

  return (
    <Menu
      opened={opened}
      onChange={setOpened}
      transitionProps={{ exitDuration: 0 }}
      withinPortal
      width={width}
    >
      <Menu.Target>
        <Tooltip label='Currency' withArrow>
          <a
            href='#'
            aria-label='Select currency'
            className={`flex items-center justify-between cursor-pointer ${className}`}
            onClick={(e) => {
              e.preventDefault()
              setOpened((o) => !o)
            }}
          >
            {selected?.name === 'US Dollar' ? (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='18'
                height='18'
                viewBox='0 0 14 14'
                fill='none'
              >
                <path
                  d='M7 3.5V10.5M5.25 8.85617L5.76275 9.24058C6.44583 9.75333 7.55358 9.75333 8.23725 9.24058C8.92092 8.72783 8.92092 7.89717 8.23725 7.38442C7.896 7.12775 7.448 7 7 7C6.57708 7 6.15417 6.87167 5.83158 6.61558C5.18642 6.10283 5.18642 5.27217 5.83158 4.75942C6.47675 4.24667 7.52325 4.24667 8.16842 4.75942L8.4105 4.95192M12.25 7C12.25 7.68944 12.1142 8.37213 11.8504 9.00909C11.5865 9.64605 11.1998 10.2248 10.7123 10.7123C10.2248 11.1998 9.64605 11.5865 9.00909 11.8504C8.37213 12.1142 7.68944 12.25 7 12.25C6.31056 12.25 5.62787 12.1142 4.99091 11.8504C4.35395 11.5865 3.7752 11.1998 3.28769 10.7123C2.80018 10.2248 2.41347 9.64605 2.14963 9.00909C1.8858 8.37213 1.75 7.68944 1.75 7C1.75 5.60761 2.30312 4.27226 3.28769 3.28769C4.27226 2.30312 5.60761 1.75 7 1.75C8.39239 1.75 9.72774 2.30312 10.7123 3.28769C11.6969 4.27226 12.25 5.60761 12.25 7Z'
                  stroke='white'
                  strokeWidth='0.875'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            ) : (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='18'
                height='18'
                viewBox='0 0 14 14'
                fill='none'
              >
                <path
                  d='M12.25 7C12.25 7.68944 12.1142 8.37213 11.8504 9.00909C11.5865 9.64605 11.1998 10.2248 10.7123 10.7123C10.2248 11.1998 9.64605 11.5865 9.00909 11.8504C8.37213 12.1142 7.68944 12.25 7 12.25C6.31056 12.25 5.62787 12.1142 4.99091 11.8504C4.35395 11.5865 3.7752 11.1998 3.28769 10.7123C2.80018 10.2248 2.41347 9.64605 2.14963 9.00909C1.8858 8.37213 1.75 7.68944 1.75 7C1.75 5.60761 2.30312 4.27226 3.28769 3.28769C4.27226 2.30312 5.60761 1.75 7 1.75C8.39239 1.75 9.72774 2.30312 10.7123 3.28769C11.6969 4.27226 12.25 5.60761 12.25 7Z'
                  stroke='white'
                  strokeWidth='0.875'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
                <path
                  d='M6.51465 4.84961V5.50293H4.48633V6.70117H6.375V7.35254H4.48633V8.55664H6.54004V9.20996H3.74414V4.84961H6.51465ZM8.94727 4.79199C9.15804 4.79199 9.34992 4.82662 9.52051 4.89746C9.69108 4.96775 9.83566 5.06431 9.95312 5.1875L10.0352 5.28223C10.1118 5.38035 10.1697 5.48835 10.208 5.60645C10.2592 5.76445 10.2687 5.93005 10.2383 6.10254L10.2305 6.15137L10.1816 6.14355L9.58008 6.04785L9.53418 6.04004L9.53906 5.99316C9.5521 5.86526 9.53082 5.76383 9.48145 5.68555C9.42995 5.60405 9.35876 5.54277 9.26758 5.50098C9.17455 5.45839 9.07585 5.43654 8.9707 5.43652C8.86103 5.43652 8.75606 5.46182 8.65625 5.51367L8.65723 5.51465C8.58495 5.55269 8.52195 5.60555 8.46875 5.6748L8.41895 5.74902C8.35957 5.85282 8.32817 5.9888 8.32812 6.16016L8.35547 6.88867H9.33691V7.54199H8.37891L8.39453 7.95312V8.08887C8.38883 8.22053 8.36492 8.34254 8.32227 8.4541C8.3083 8.49026 8.2908 8.52408 8.27148 8.55664H10.21V9.20996H7.19824V8.55664H7.44727C7.50307 8.55354 7.54623 8.52671 7.5791 8.46777C7.61579 8.40195 7.64114 8.32349 7.6543 8.23145L7.66895 8.09473C7.67161 8.05191 7.67222 8.01131 7.6709 7.97363L7.65625 7.54199H7.18359V6.88867H7.63477L7.61035 6.16211C7.59904 5.86394 7.65378 5.61014 7.7793 5.4043H7.78027C7.90567 5.2011 8.0729 5.04706 8.28125 4.94434C8.48778 4.8426 8.71003 4.79206 8.94727 4.79199Z'
                  fill='white'
                  stroke='white'
                  strokeWidth='0.1'
                />
              </svg>
            )}
            <RiArrowDownSLine size={16} />
          </a>
        </Tooltip>
      </Menu.Target>

      {/* Added rounded-[12px] for dropdown */}
      <Menu.Dropdown className='!rounded-xl'>{menuItems}</Menu.Dropdown>
    </Menu>
  )
}

export default CurrencySelector
